import {createI18n} from 'vue-i18n';
import {DEFAULT_LOCALE, parseLocale, resolveLocale, type Locale} from '@afroep/core/domain/locale';
import en from './locales/en.json';
import nl from './locales/nl.json';

/**
 * The interface's language. Two things decide it, in this order:
 *
 * 1. the preference stored on the account, applied by the session store once `/api/me`
 *    answers — it is what the backend writes mail in, so the two must not disagree;
 * 2. what this browser last used, or failing that what the browser itself asks for.
 *
 * Step 2 has to work before anyone has signed in: the login screen, the invitation flow
 * and the password reset are all read by people the API will not tell us anything about.
 */
export const LOCALE_STORAGE_KEY = 'afroep.locale';

/** `localStorage` throws in a private window in some browsers; a language is not worth failing over. */
const readStored = (): string | null => {
  try {
    return globalThis.localStorage?.getItem(LOCALE_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
};

const writeStored = (locale: Locale): void => {
  try {
    globalThis.localStorage?.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Nothing to do: the choice still applies to this page, it just will not be remembered.
  }
};

export const preferredLocale = (): Locale => {
  // A stored value only wins if we can still render it. One we dropped, or that some
  // other tool wrote, falls through to the browser rather than dragging the reader back
  // to the default.
  return parseLocale(readStored()) ?? resolveLocale(globalThis.navigator?.language);
};

export const messages = {nl, en};

/**
 * Dates follow the language, not the machine. A Dutch reader gets 31-12-2026 and an
 * English one 31/12/2026 — `Intl` decides which, so neither is hand-rolled here.
 *
 * The time zone is deliberately left to the browser: everyone using this tool is standing
 * in the same grid, and an audit line reading as local time is what an operator expects.
 */
/**
 * Deliberately empty. Every date a user reads goes through `composables/useDates`, because
 * `Intl.DateTimeFormat` — which is what `d()` wraps — cannot write an English ordinal, and
 * `8th September 2026` is the house format. Leaving formats here as well would offer a
 * second way to render a date that quietly disagrees with the first.
 */
export const datetimeFormats = {nl: {}, en: {}};

export const i18n = createI18n({
  legacy: false,
  locale: preferredLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages,
  datetimeFormats,
  // A key that reaches a user as a raw identifier is a bug, and a silent one in
  // production. The suite asserts the two catalogues match, so this is the last net.
  missingWarn: import.meta.env.DEV,
  fallbackWarn: import.meta.env.DEV,
});

export const currentLocale = (): Locale => resolveLocale(i18n.global.locale.value);

/**
 * Switches the language everywhere it is visible at once — the strings, the `lang`
 * attribute screen readers use to pick a pronunciation, and what this browser will
 * remember next time. Persisting it to the account is the caller's job; only the caller
 * knows whether anybody is signed in.
 */
export const applyLocale = (candidate: string | null | undefined): Locale => {
  const locale = resolveLocale(candidate);
  i18n.global.locale.value = locale;
  writeStored(locale);
  globalThis.document?.documentElement?.setAttribute('lang', locale);
  return locale;
};

/**
 * `t` outside a component — the router, a store, a composable with no instance. Narrowed
 * to the one call shape we use, so this module does not export a type that only exists
 * inside vue-i18n's own dependency tree.
 */
export const translate = (key: string, named?: Record<string, unknown>): string =>
  named ? i18n.global.t(key, named) : i18n.global.t(key);
